import React, { useEffect, useMemo, useState, useRef } from "react";
import Select from "react-select";
import axiosInstance from "services/AxiosInstance";

const ACTIVOS_ENDPOINT = "/api/v3/zonas-sociales/activos/";

const activosCache = new Map();
const activosInFlight = new Map();

const buildCacheKey = (params) =>
  JSON.stringify(params || {});

const ActivosSelectReact = React.forwardRef(
  (
    {
      value = "",
      onChange,
      name = "activo_id",
      id,
      label = "Activo",
      showLabel = true,
      placeholder = "Selecciona un activo",
      allowAllOption = false,
      allOptionValue = "all",
      allOptionLabel = "Todos los activos",
      options: externalOptions,
      params = {},
      disabled = false,
      containerClassName,
      labelClassName = "form-label fw-semibold",
      helperText,
      isLoading: externalLoading,
    },
    ref,
  ) => {
    const [activos, setActivos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      if (externalOptions) {
        setActivos(externalOptions);
        return;
      }

      const fetchActivos = async () => {
        const cacheKey = buildCacheKey(params);
        
        const cached = activosCache.get(cacheKey);
        if (cached) {
          setActivos(cached);
          return;
        }

        const inFlight = activosInFlight.get(cacheKey);
        if (inFlight) {
          try {
            const data = await inFlight;
            if (isMountedRef.current) {
              setActivos(data);
            }
            return;
          } catch (e) {
            // continue to fetch
          }
        }

        setIsLoading(true);
        
        const fetchPromise = (async () => {
          try {
            const response = await axiosInstance.get(ACTIVOS_ENDPOINT, {
              params: { ...params, page_size: 100 },
            });
            const results = Array.isArray(response.data)
              ? response.data
              : response.data?.results || [];
            activosCache.set(cacheKey, results);
            return results;
          } catch (error) {
            console.error("Error fetching activos:", error);
            return [];
          }
        })();

        activosInFlight.set(cacheKey, fetchPromise);

        try {
          const results = await fetchPromise;
          if (isMountedRef.current) {
            setActivos(results);
          }
        } finally {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
          activosInFlight.delete(cacheKey);
        }
      };

      fetchActivos();
    }, [externalOptions, params]);

    const effectiveLoading = externalLoading || isLoading;

    const normalizedOptions = useMemo(() => {
      const base = Array.isArray(activos) ? activos : [];
      const map = new Map();

      base.forEach((option) => {
        if (!option) return;
        const id = String(option.uuid ?? option.id ?? option.value ?? "");
        const nombre = String(
          option.nombre ??
            option.label ??
            option.name ??
            option.consecutivo ??
            "",
        );
        const label = option.consecutivo
          ? `${option.consecutivo} - ${nombre}`
          : nombre;
        if (!id) return;
        map.set(id, { value: id, label });
      });

      if (allowAllOption) {
        map.set(String(allOptionValue), {
          value: String(allOptionValue),
          label: allOptionLabel,
        });
      }

      return Array.from(map.values());
    }, [activos, allowAllOption, allOptionValue, allOptionLabel]);

    const selectedValue = useMemo(() => {
      if (!value) return null;
      return (
        normalizedOptions.find((opt) => opt.value === String(value)) || null
      );
    }, [normalizedOptions, value]);

    const handleChange = (option) => {
      const newValue = option?.value || "";
      onChange?.(newValue, option);
    };

    const resolvedId = id || name || "activo-select";

    return (
      <div className={containerClassName}>
        {showLabel && (
          <label className={labelClassName} htmlFor={resolvedId}>
            {label}
          </label>
        )}

        <Select
          ref={ref}
          id={resolvedId}
          name={name}
          value={selectedValue}
          onChange={handleChange}
          options={normalizedOptions}
          isClearable={!allowAllOption}
          isSearchable
          placeholder={effectiveLoading ? "Cargando..." : placeholder}
          isDisabled={disabled || effectiveLoading}
          isLoading={effectiveLoading}
          noOptionsMessage={() => "No hay activos"}
        />

        {helperText && <div className="form-text">{helperText}</div>}
      </div>
    );
  },
);

ActivosSelectReact.displayName = "ActivosSelectReact";

export default ActivosSelectReact;
